/**
 * Status panel showing live connection state for the gateway and Sharedo API.
 *
 * Displays:
 * - Gateway connection status (from useGateway)
 * - Sharedo API connectivity (from health endpoint)
 * - Auth session info (dev mode, instance URL)
 * - Document content control count (Office.js Word API)
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Text,
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useGateway } from '../../api/useGateway';
import { useAuth } from '../../auth/auth-context';
import { scanControlCounts } from '../../office/sdt-reader';
import type { HealthStatus } from '../../api/gateway-client';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '13px',
  },
  statusRowRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  healthDetails: {
    padding: '8px 0',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  errorText: {
    padding: '4px 0',
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px 0',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  infoLabel: {
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  placeholder: {
    padding: '24px 16px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
});

/** Status panel showing connection state, sync history, and validation results. */
export const StatusPanel: React.FC = () => {
  const styles = useStyles();
  const client = useGateway();
  const { isDevMode, instanceUrl, user } = useAuth();

  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [docInfo, setDocInfo] = useState<{ total: number; tagged: number; reserveTags: number } | null>(null);
  const [docScanning, setDocScanning] = useState(false);

  const isGatewayConnected = client !== null;
  const sharedoConnected = health?.adapters?.['sharedo'] === true;

  const checkHealth = useCallback(async () => {
    if (!client) return;

    setHealthLoading(true);
    setHealthError(null);

    try {
      const result = await client.getHealth();
      setHealth(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Health check failed';
      setHealthError(message);
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  }, [client]);

  const scanDocument = useCallback(async () => {
    setDocScanning(true);
    try {
      const info = await scanControlCounts();
      setDocInfo(info);
    } finally {
      setDocScanning(false);
    }
  }, []);

  // Check health on mount and whenever the client instance changes
  useEffect(() => {
    if (!client) {
      setHealth(null);
      setHealthError(null);
      return;
    }

    let cancelled = false;

    (async () => {
      setHealthLoading(true);
      setHealthError(null);
      try {
        const result = await client.getHealth();
        if (!cancelled) setHealth(result);
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Health check failed';
          setHealthError(message);
          setHealth(null);
        }
      } finally {
        if (!cancelled) setHealthLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [client]);

  return (
    <div className={styles.container}>
      <Card size="small">
        <CardHeader
          header={<Text weight="semibold">System Status</Text>}
        />
      </Card>

      {/* Gateway connection status */}
      <div className={styles.statusRow}>
        <Text>Gateway</Text>
        <div className={styles.statusRowRight}>
          {isGatewayConnected ? (
            <Badge appearance="filled" color="success" size="small">Connected</Badge>
          ) : (
            <Badge appearance="filled" color="danger" size="small">Disconnected</Badge>
          )}
        </div>
      </div>

      {/* Sharedo API status (derived from health endpoint) */}
      <div className={styles.statusRow}>
        <Text>Sharedo API</Text>
        <div className={styles.statusRowRight}>
          {healthLoading ? (
            <Spinner size="tiny" />
          ) : sharedoConnected ? (
            <Badge appearance="filled" color="success" size="small">Connected</Badge>
          ) : health && !sharedoConnected ? (
            <Badge appearance="filled" color="warning" size="small">Unavailable</Badge>
          ) : (
            <Badge appearance="filled" color="danger" size="small">Disconnected</Badge>
          )}
        </div>
      </div>

      {/* Document content controls */}
      <div className={styles.statusRow}>
        <Text>Document SDT Tags</Text>
        <div className={styles.statusRowRight}>
          {docScanning ? (
            <Spinner size="tiny" />
          ) : docInfo === null ? (
            <Badge appearance="filled" color="warning" size="small">Not Scanned</Badge>
          ) : docInfo.total < 0 ? (
            <Badge appearance="filled" color="danger" size="small">Unavailable</Badge>
          ) : docInfo.reserveTags > 0 ? (
            <Badge appearance="filled" color="success" size="small">
              {docInfo.reserveTags} reserve / {docInfo.tagged} tagged
            </Badge>
          ) : docInfo.tagged > 0 ? (
            <Badge appearance="filled" color="warning" size="small">
              {docInfo.tagged} tagged, 0 reserve
            </Badge>
          ) : (
            <Badge appearance="filled" color="warning" size="small">
              {docInfo.total} controls, 0 tagged
            </Badge>
          )}
        </div>
      </div>

      {/* Auth mode indicator */}
      <div className={styles.statusRow}>
        <Text>Auth Mode</Text>
        <div className={styles.statusRowRight}>
          {isDevMode ? (
            <Badge appearance="filled" color="warning" size="small">Dev Mode</Badge>
          ) : isGatewayConnected ? (
            <Badge appearance="filled" color="success" size="small">OAuth</Badge>
          ) : (
            <Badge appearance="ghost" color="informative" size="small">Not Signed In</Badge>
          )}
        </div>
      </div>

      {/* Health details / error */}
      {healthError && (
        <div className={styles.errorText}>
          <Text size={200}>{healthError}</Text>
        </div>
      )}
      {health && (
        <div className={styles.healthDetails}>
          <Text size={200}>
            Gateway v{health.version} — {health.status}
          </Text>
        </div>
      )}

      {/* Session info */}
      {isGatewayConnected && (
        <div className={styles.infoSection}>
          <Text className={styles.infoLabel}>Session Info</Text>
          {user?.displayName && <Text size={200}>User: {user.displayName}</Text>}
          {instanceUrl && <Text size={200}>Instance: {instanceUrl}</Text>}
        </div>
      )}

      {/* Action buttons */}
      {isGatewayConnected && (
        <>
          <Button
            appearance="subtle"
            size="small"
            disabled={healthLoading}
            onClick={checkHealth}
          >
            {healthLoading ? 'Checking...' : 'Check Health'}
          </Button>
          <Button
            appearance="subtle"
            size="small"
            disabled={docScanning}
            onClick={scanDocument}
          >
            {docScanning ? 'Scanning...' : 'Scan Document'}
          </Button>
        </>
      )}

      {/* Placeholder when not connected */}
      {!isGatewayConnected && (
        <div className={styles.placeholder}>
          <Text>Sign in to check system status and sync history.</Text>
        </div>
      )}
    </div>
  );
};
